import * as React from 'react';
import { Rnd, RndDragCallback } from 'react-rnd';
import { Dispatch } from 'react-redux';
import * as elementResizeDetectorMaker from 'element-resize-detector';

import { StoreState } from '../../../reducers';
import { StyledEditPanel, StyledEditPanelInner, StyledIconMove } from './styles';
import { IGNORE_PAGE_CLICK_CLASSNAME, EDIT_PANEL_CLASSNAME } from '../../../containers/Editor/constants';
import { HEADER_HEIGHT, PAGE_PADDING } from '../styles';
import Portal from '../../Portal';
import EditPanelLabel from './Label';
import { PanelType } from './constants';
import { moveEditPanel } from '../../../containers/Editor/actions';
import Utils from '../../../utils';

interface EditPanelProps {
	defaultType: PanelType;
	panels: { [k: string]: React.ReactNode };
	x: number | null;
	y: number | null;
	activeItemId: string;
	isDisabled: boolean;
	dispatch: Dispatch<StoreState>;
	className?: string;
}

export const EDIT_PANEL_WIDTH = 380;
const GAP = 10;

const EditPanel = ({
	defaultType,
	panels,
	x,
	y,
	activeItemId,
	isDisabled,
	dispatch,
	className,
}: EditPanelProps) => {
	const panelElement = React.useRef<HTMLDivElement>(null);
	const [type, setType] = React.useState(defaultType);
	const [panelHeight, setPanelHeight] = React.useState(0);

	const handleDragStop: RndDragCallback = (e, { x, y }) => {
		dispatch(moveEditPanel(x, y));
	};

	const setPosition = () => {
		const {
			offsetWidth: panelWidth,
			offsetHeight: panelHeight
		} = panelElement.current as HTMLDivElement;
		const isFirstDisplay = x === null && y === null;

		if (isFirstDisplay) {
			let defaultX = window.innerWidth - panelWidth - PAGE_PADDING;
			let defaultY = HEADER_HEIGHT;

			if (!activeItemId) {
				dispatch(moveEditPanel(defaultX, defaultY));
				return;
			}

			const activeItem = document.querySelector(`#${activeItemId}`);

			if (activeItem) {
				const { top, left, right } = activeItem.getBoundingClientRect();
				const panelBottom = defaultY + panelHeight;
				const panelLeft = defaultX;
				const isActiveItemOverlapped = panelBottom > top && panelLeft < right;

				if (isActiveItemOverlapped) {
					// display panel to the left of the item
					defaultX = left - panelWidth - GAP;
				}

				defaultX = defaultX < 0 ? 0 : defaultX;
				defaultY = defaultY < 0 ? 0 : defaultY;
			}

			dispatch(moveEditPanel(defaultX, defaultY));
			return;
		}

		const shouldCorrectX = x !== null && x + panelWidth > window.innerWidth;
		const shouldCorrectY = y !== null && y + panelHeight > window.innerHeight;

		if (!shouldCorrectX && !shouldCorrectY) {
			return;
		}

		let correctedX = shouldCorrectX ? window.innerWidth - panelWidth : x as number;
		let correctedY = shouldCorrectY ? window.innerHeight - panelHeight : y as number;
		correctedX = correctedX < 0 ? 0 : correctedX;
		correctedY = correctedY < 0 ? 0 : correctedY;

		dispatch(moveEditPanel(correctedX, correctedY));
	};

	React.useEffect(() => {
		setType(defaultType);
	}, [defaultType]);

	React.useEffect(() => {
		const resizeDetector = elementResizeDetectorMaker({
			strategy: 'scroll',
			callOnAdd: false,
		});

		if (panelElement.current) {
			resizeDetector.listenTo(panelElement.current, (el) => {
				setPanelHeight(el.offsetHeight);
			});
		}

		return () => {
			if (panelElement.current) {
				resizeDetector.uninstall(panelElement.current);
			}
		};
	}, []);

	React.useEffect(() => {
		setPosition();

		// Standard window 'resize' event isn't triggered in webview on iOS13, it should be listened on window.visualViewport
		if (window.visualViewport) {
			window.visualViewport.addEventListener('resize', setPosition);
		} else {
			window.addEventListener('resize', setPosition);
		}

		return () => {
			if (window.visualViewport) {
				window.visualViewport.removeEventListener('resize', setPosition);
			} else {
				window.removeEventListener('resize', setPosition);
			}
		};
	}, [x, y, panelHeight]);

	const position = {
		x: x === null ? 0 : x,
		y: y === null ? 0 : y,
	};

	return (
		<Portal parentNode={document.body}>
			<Rnd
				position={position}
				onDragStop={handleDragStop}
				bounds="parent"
				cancel=".prevent-drag"
				enableResizing={false}
			>
				<StyledEditPanel
					ref={panelElement}
					className={`${IGNORE_PAGE_CLICK_CLASSNAME} ${EDIT_PANEL_CLASSNAME} ${className || ''}`}
					isDisabled={isDisabled}
				>
					<StyledIconMove>
						{Utils.getSvgIcon('iconset_6dots')}
					</StyledIconMove>
					<EditPanelLabel
						type={type}
						availableTypes={Object.keys(panels) as PanelType[]}
						onClick={setType}
					/>
					<StyledEditPanelInner className="prevent-drag">
						{panels[type]}
					</StyledEditPanelInner>
				</StyledEditPanel>
			</Rnd>
		</Portal>
	);
};

export default EditPanel;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/EditPanel/index.tsx